"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { Lock, LogIn, PlayCircle } from "lucide-react";
import { useDict } from "@/components/i18n/DictProvider";
import { formatPrice } from "@/lib/format";
import { langPath, type Lang } from "@/lib/i18n";
import type { Access } from "./paywall";

/**
 * The start cards' shared vocabulary for an `Access` (./paywall.ts): the price
 * line and the one primary button. The course, video/podcast and event cards
 * each decide only what "open" and "start" DO for their type; what a locked
 * item offers, and how it reads, lives here once.
 */

// Editorial gold pill — the one primary action on every start card.
export const ctaClass =
  "inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent text-accent-foreground font-display font-bold uppercase tracking-[0.12em] text-[12px] md:text-[13px] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 active:brightness-100 disabled:pointer-events-none disabled:opacity-60";
export const ctaStyle = {
  boxShadow:
    "0 2px 4px rgb(0 0 0 / 0.10), 0 8px 18px -6px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 45%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 70%, transparent), inset 0 -1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 40%) 20%, transparent)",
};
const disabledCtaClass =
  "inline-flex w-full items-center justify-center gap-2 h-12 rounded-full bg-accent/40 text-primary/70 font-display font-bold uppercase tracking-[0.12em] text-[12px] md:text-[13px] cursor-not-allowed";

/**
 * The price line. A price only where this reader must buy; "Samo za
 * naročnike" for a subscription; "Free" for an item that opens without paying.
 * An item the reader already owns (open, not free) says nothing — the caller
 * may put its own status there.
 */
export function AccessPrice({ access, free }: { access: Access; free: boolean }) {
  const t = useDict();
  if (access.kind === "buy") {
    return (
      <div>
        <p className="font-display text-[26px] font-extrabold text-foreground tabular-nums tracking-[-0.01em]">
          {formatPrice(access.price)}
        </p>
        <p className="mt-0.5 text-[11.5px] text-foreground/55">{t.detail.oneTimeLifetime}</p>
      </div>
    );
  }
  if (access.kind === "subscribe") {
    return (
      <p className="font-display text-[16px] font-bold text-foreground">
        {t.detail.subscribersOnly}
      </p>
    );
  }
  if (!free) return null;
  return <p className="font-display text-[22px] font-extrabold text-accent">{t.detail.free}</p>;
}

/**
 * The primary button for a locked item, or the caller's own for an open one.
 * `onStart` makes the free enrolment a PREMIUM item marked free still needs
 * (kind "start"); `loginLabel` lets a course say "Sign in to start".
 */
export function AccessCta({
  access,
  lang,
  moduleId,
  open,
  onStart,
  startLabel,
  loginLabel,
  pending = false,
}: {
  access: Access;
  lang: Lang;
  moduleId: number;
  /** What an open item's button does — a link to the player, "Start", or
   *  nothing when the content is already on the page. */
  open: React.ReactNode;
  onStart: () => void;
  startLabel: string;
  loginLabel?: string;
  pending?: boolean;
}) {
  const t = useDict();
  const router = useRouter();
  switch (access.kind) {
    case "open":
      return <>{open}</>;
    case "buy":
      return (
        <Link href={langPath(lang, `/checkout/${moduleId}`)} className={ctaClass} style={ctaStyle}>
          <Lock className="size-4" aria-hidden />
          {t.portal.payperBuyLabel}
        </Link>
      );
    case "subscribe":
      return (
        <Link href={langPath(lang, "/profil/narocnine")} className={ctaClass} style={ctaStyle}>
          <Lock className="size-4" aria-hidden />
          {t.detail.subscribe}
        </Link>
      );
    case "login":
      return (
        <button
          type="button"
          onClick={() => router.push(loginHref(lang))}
          className={ctaClass}
          style={ctaStyle}
        >
          <LogIn className="size-4" aria-hidden />
          {loginLabel ?? t.detail.logInCta}
        </button>
      );
    case "start":
      return (
        <button
          type="button"
          onClick={onStart}
          disabled={pending}
          className={ctaClass}
          style={ctaStyle}
        >
          <PlayCircle className="size-4" aria-hidden />
          {pending ? "…" : startLabel}
        </button>
      );
    case "restricted":
      return (
        <button type="button" disabled className={disabledCtaClass}>
          <Lock className="size-4" aria-hidden />
          {t.portal.gatedUnavailable}
        </button>
      );
  }
}

/** The sign-in page, coming back here afterwards. Called on click, so it
 *  carries the page the reader is on. */
export function loginHref(lang: Lang): string {
  return `${langPath(lang, "/login")}?redirect=${encodeURIComponent(window.location.pathname)}`;
}
